import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Form } from 'antd';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import RidesSearchForm from '../home/components/searchTrainTickets/components/RidesSearchForm';
import SearchTitle from './components/SearchTitle';
import './Search.scss';
import SearchRidesList from './components/SearchRidesList';
import SearchRidesForm from './components/searchRidesForm/SearchRidesForm';
import useQuery from '../../../hooks/useQuery';
import {
  searchTicket,
} from '../../../../services/customer/ticketBookingServices';
import TicketSearchFormDrawer from '../../components/drawers/ticketSearchDrawers/ticketSearchFormDrawer/TicketSearchFormDrawer';
import { formTicketSearchValues, validateTicketSearch } from '../../../../utils/functions/ticketSearch';
import uid from '../../../../utils/functions/uid';
import useTranslation from '../../../hooks/translation/useTranslation';
import NotFound from './components/NotFound';
import { useAuth } from '../../../contexts/userContext/userContext';
import ContinueDrawer from '../../components/drawers/ticketSearchDrawers/continueDrawer/ContinueDrawer';
import showErrorMessageModal from '../../components/modals/showErrorMessageModal/showErrorMessageModal';

function Search() {
  const resultRef = useRef(null);
  const {
    ticketBookingOptions: ticketSearchData,
    user,
  } = useAuth();

  const query = useQuery();
  const [form] = Form.useForm();
  const [seatFormationForm] = Form.useForm();
  const [chosenRides, setChosenRides] = useState({
    departure: null,
    return: null,
  });
  const [errors, setErrors] = useState(null);
  const [rides, setRides] = useState({
    departureRides: [],
    returnRides: [],
  });
  const navigate = useNavigate();
  const location = useLocation();
  const guid = useMemo(() => uid(), []);
  const { currentLanguage, trans, transNotification } = useTranslation();

  const searchValues = useMemo(() => {
    const startStationCode = query.get('startStationCode');
    const endStationCode = query.get('endStationCode');
    const departureDate = moment.utc(query.get('departureDateFrom'), 'YYYY-MM-DD', true);
    const returnDate = query.get('returnDateFrom') ? moment.utc(query.get('returnDateFrom'), 'YYYY-MM-DD', true) : null;
    form.setFieldsValue({
      startStationCode: startStationCode && startStationCode.length !== 0
        ? startStationCode : null,
      endStationCode: endStationCode && endStationCode.length !== 0
        ? endStationCode : null,
      departureDateFrom: departureDate.isValid() ? departureDate.add(moment().utcOffset(), 'minutes').toDate() : new Date(),
      ...(returnDate && {
        returnDateFrom: returnDate.isValid() ? returnDate.toDate() : new Date(),
      }),
      standard_passengers: Number(query.get('standard_passengers') || 1),
      child_passengers: Number(query.get('child_passengers') || 0),
      disabled_passengers: Number(query.get('disabled_passengers') || 0),
      routeType: 0,
    });
    return {
      startStationCode: startStationCode && startStationCode.length !== 0
        ? startStationCode : null,
      endStationCode: endStationCode && endStationCode.length !== 0
        ? endStationCode : null,
      departureDateFrom: departureDate.isValid() ? departureDate.add(moment().utcOffset(), 'minutes').toDate() : new Date(),
      ...(returnDate && {
        returnDateFrom: returnDate.isValid() ? returnDate.toDate() : new Date(),
      }),
      standard_passengers: Number(query.get('standard_passengers') || 1),
      child_passengers: Number(query.get('child_passengers') || 0),
      disabled_passengers: Number(query.get('disabled_passengers') || 0),
      routeType: 0,
    };
  }, [location.search]);

  const setFormData = async () => {
    form.setFieldsValue(searchValues);
    seatFormationForm.setFieldValue('seatFormation', 'auto');
  };

  const fetchAvailableRides = async (values) => {
    if (
      searchValues?.startStationCode
          && searchValues?.endStationCode
          && searchValues?.departureDateFrom
    ) {
      try {
        const res = await searchTicket(
          values ?? formTicketSearchValues({ ...searchValues }, navigate),
        );
        if (res?.[0]?.length === 0 && (res?.[1] || [])?.length === 0) {
          if (!transNotification('rides_not_found')) {
            setTimeout(() => {
              showErrorMessageModal({
                errorText: transNotification('rides_not_found') || trans('rides_not_found'),
              });
            }, 1500);
          } else {
            showErrorMessageModal({
              errorText: transNotification('rides_not_found'),
            });
          }
        } else if (
          [...(res?.[0] || []), ...(res?.[1] || [])]?.every(
            (ride) => ride?.availableSeatsClasses?.length === 0
                && ride?.availableSeatsGroups?.length === 0,
          )
        ) {
          if (!transNotification('rides_not_found')) {
            setTimeout(() => {
              showErrorMessageModal({
                errorText: transNotification('tickets_sold') || trans('tickets_sold'),
              });
            }, 1500);
          } else {
            showErrorMessageModal({
              errorText: transNotification('tickets_sold'),
            });
          }
        }
        setRides({
          departureRides: res[0],
          returnRides: res?.[1] ?? [],
        });
        setChosenRides({
          departure: null,
          return: null,
        });
        if (resultRef?.current) {
          resultRef.current.scrollIntoView({
            behavior: 'smooth',
          });
        }
      } catch {
        //
      }
    }
  };

  useEffect(() => {
    if (ticketSearchData) {
      setFormData();
      fetchAvailableRides();
    }
  }, [searchValues, ticketSearchData, user?.type]);

  const handleDepartureRideChoose = (ride) => {
    if (!ride) {
      setChosenRides((prev) => ({
        ...prev,
        departure: null,
      }));
      return;
    }
    setChosenRides((prev) => ({
      ...prev,
      departure: ride,
    }));
    const sessionRides = sessionStorage.getItem(guid)
      ? JSON.parse(sessionStorage.getItem(guid))
      : {};
    sessionRides.departure = {
      availableSeatsClasses: ride.availableSeatsClasses,
      chosenAvailableSeatsClasses: ride.availableSeatsClasses
        ?.filter((rideClass) => ride.chosenClasses?.find((key) => `${key}` === `${rideClass.seatClass.id}`)),
      availableSeatsGroups: ride?.availableSeatsGroups,
      chosenAvailableSeatsGroups: ride?.availableSeatsGroups
        ?.filter((rideClass) => ride.chosenClasses?.find((key) => `${key}` === `group-${rideClass.seatClass.id}`)),
      startStation: {
        ...ride?.startStation,
        ...ride?.startStation?.station,
      },
      endStation: {
        ...ride?.endStation,
        ...ride?.endStation?.station,
      },
      id: ride.id,
      startDate: ride?.startDate,
      rideNumber: ride?.rideNumber,
      rideActualStation: ride?.rideActualStation,
      rideStartStation: ride?.rideStartStation,
      rideEndStation: ride?.rideEndStation,
      searchedDisabled: !!+query.get('disabled_passengers'),
    };
    sessionStorage.setItem(guid, JSON.stringify(sessionRides));
  };

  const handleReturnRideChoose = (ride) => {
    if (!ride) {
      setChosenRides(
        (prev) => ({
          ...prev,
          return: null,
        }),
      );
      return;
    }
    setChosenRides((prev) => ({
      ...prev,
      return: ride,
    }));
    const sessionRides = sessionStorage.getItem(guid)
      ? JSON.parse(sessionStorage.getItem(guid))
      : {};
    sessionRides.return = {
      availableSeatsClasses: ride.availableSeatsClasses,
      chosenAvailableSeatsClasses: ride.availableSeatsClasses
        ?.filter((rideClass) => ride.chosenClasses?.find((key) => `${key}` === `${rideClass.seatClass.id}`)),
      availableSeatsGroups: ride?.availableSeatsGroups,
      chosenAvailableSeatsGroups: ride?.availableSeatsGroups
        ?.filter((rideClass) => ride.chosenClasses?.find((key) => `${key}` === `group-${rideClass.seatClass.id}`)),
      startStation: {
        ...ride?.startStation,
        ...ride?.startStation?.station,
      },
      endStation: {
        ...ride?.endStation,
        ...ride?.endStation?.station,
      },
      id: ride.id,
      startDate: ride?.startDate,
      endDate: ride?.endDate,
      rideNumber: ride?.rideNumber,
      rideActualStation: ride?.rideActualStation,
      rideStartStation: ride?.rideStartStation,
      rideEndStation: ride?.rideEndStation,
      searchedDisabled: !!+query.get('disabled_passengers'),
    };
    sessionStorage.setItem(guid, JSON.stringify(sessionRides));
  };

  const handleAfterSwitch = () => {
    setRides({
      departureRides: [],
      returnRides: [],
    });
    validateTicketSearch({
      values: form.getFieldsValue(),
      setErrors,
      navigate,
      currentLanguage,
      trans,
    });
  };

  const startStation = ticketSearchData?.stations?.find((station) => `${station?.value}` === `${searchValues.startStationCode}`);
  const endStation = ticketSearchData?.stations?.find((station) => `${station?.value}` === `${searchValues.endStationCode}`);
  return (
    <div className="flex flex-col gap-[60px]">
      <RidesSearchForm
        errors={errors}
        handleSubmit={(values) => validateTicketSearch({
          values,
          setErrors,
          navigate,
          currentLanguage,
          trans,
        })}
        options={ticketSearchData}
        inputsClassName="bg-white"
        form={form}
        handleAfterSwitch={handleAfterSwitch}
      />
      {(searchValues?.startStationCode && searchValues?.endStationCode) && (
        <SearchTitle
          rideStations={`${startStation?.[`label_${currentLanguage}`]} - ${endStation?.[`label_${currentLanguage}`]}`}
          date={moment.utc(searchValues?.departureDateFrom, true).format('DD.MM.YYYY')}
          forwardRef={resultRef}
        />
      )}
      <div className="flex gap-5 flex-col lg:flex-row">
        <div className="flex flex-col gap-5 w-full">
          <NotFound rides={rides} />
          <SearchRidesList
            setChosenRide={handleDepartureRideChoose}
            chosenRide={chosenRides.departure}
            rides={rides?.departureRides}
            seatFormationForm={seatFormationForm}
          />
          {(searchValues?.startStationCode
              && searchValues?.endStationCode
              && searchValues?.returnDateFrom) && (
              <SearchTitle
                rideStations={`${endStation?.[`label_${currentLanguage}`]} - ${startStation?.[`label_${currentLanguage}`]}`}
                date={moment.utc(searchValues?.returnDateFrom, true).format('DD.MM.YYYY')}
              />
          )}
          <SearchRidesList
            setChosenRide={handleReturnRideChoose}
            chosenRide={chosenRides.return}
            rides={rides?.returnRides}
            seatFormationForm={seatFormationForm}
          />
        </div>
        <SearchRidesForm
          className="hidden lg:block"
          rideSearchForm={form}
          seatFormationForm={seatFormationForm}
          chosenRides={chosenRides}
          guid={guid}
        />
      </div>
      <TicketSearchFormDrawer
        rideSearchForm={form}
        seatFormationForm={seatFormationForm}
        chosenRides={chosenRides}
        guid={guid}
      />
      <ContinueDrawer rideSearchForm={form} chosenRides={chosenRides} />
    </div>
  );
}

export default Search;
